import axios from "axios";
import { store } from "../redux/store";
import { logout, updateTokens } from "../redux/auth/authSlice";
import { showToast } from "../componants/toast/toastService";
import endpoints from "./endpoints";

// Extracts the specific error detail from a server error payload, deliberately
// skipping `data.message` — that field is generic (e.g. "Invalid data") and is
// used as the toast title instead, while this becomes the toast description.
const getServerErrorDetail = (data) => {
  if (!data) return null;

  if (typeof data.detail === "string" && data.detail.trim()) {
    return data.detail;
  }
  if (typeof data.error === "string" && data.error.trim()) {
    return data.error;
  }
  if (data.error && typeof data.error === "object") {
    const nested = Object.values(data.error)
      .flat()
      .find((item) => typeof item === "string" && item.trim());
    if (nested) return nested;
  }
  // Nested field errors under data: { data: { field: ["msg"] } }
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    const nested = Object.values(data.data)
      .flat()
      .find((item) => typeof item === "string" && item.trim());
    if (nested) return nested;
  }
  if (data.errors && typeof data.errors === "object") {
    const nested = Object.values(data.errors)
      .flat()
      .find((item) => typeof item === "string" && item.trim());
    if (nested) return nested;
  }

  const fieldErrors = Object.entries(data)
    .filter(([key]) => !["status", "statusCode", "message", "error", "data", "errors"].includes(key))
    .map(([key, value]) => {
      const msg = Array.isArray(value) ? value[0] : value;
      return typeof msg === "string" && msg.trim() ? `${key}: ${msg}` : null;
    })
    .filter(Boolean);

  return fieldErrors.length ? fieldErrors.join(", ") : null;
};

// Name of the first field the server complained about (the key next to the
// message getServerErrorDetail picks), so the toast can scroll to that field.
const getServerErrorField = (data) => {
  if (!data) return null;

  const sources = [data.error, data.data, data.errors];

  for (const source of sources) {
    if (source && typeof source === "object" && !Array.isArray(source)) {
      const key = Object.keys(source).find((k) => {
        const value = source[k];
        return (
          (typeof value === "string" && value.trim()) ||
          (Array.isArray(value) && value.length)
        );
      });

      if (key) return key;
    }
  }

  const RESERVED = ["status", "statusCode", "message", "error", "data", "errors", "detail"];

  return (
    Object.keys(data).find(
      (k) => !RESERVED.includes(k) && (typeof data[k] === "string" || Array.isArray(data[k])),
    ) || null
  );
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// ================= Refresh Queue =================

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });

  failedQueue = [];
};

// ================= Request Interceptor =================

api.interceptors.request.use(
  (config) => {
    const token = store.getState()?.auth?.access_token;
    const allTimeZones = Intl.supportedValuesOf("timeZone");
    const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (currentTimeZone) {
      config.headers["X-TIMEZONE"] = currentTimeZone;
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ================= Response Interceptor =================

api.interceptors.response.use(
  (response) => {
    const { silent } = response.config;

    if (!silent && response.data?.message) {
      showToast({ title: "Success!", message: response.data.message }, "success");
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Network Error
    if (!error.response) {
      showToast("Network error. Please check your connection.", "error");
      return Promise.reject(error);
    }

    const { status, data } = error.response;

    // ================= Handle 401 =================

    if (status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = store.getState()?.auth?.refresh_token;

      if (!refreshToken) {
        isRefreshing = false;
        processQueue(error);

        store.dispatch(logout());

        showToast("Session expired. Please login again.", "error");

        return Promise.reject(error);
      }

      try {
        const { data: refreshData } = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}${endpoints.auth.refreshToken}`,
          {
            refresh: refreshToken,
          },
        );

        const newAccessToken = refreshData?.data?.access || refreshData?.access;

        const newRefreshToken =
          refreshData?.data?.refresh || refreshData?.refresh;

        store.dispatch(
          updateTokens({
            access_token: newAccessToken,
            ...(newRefreshToken && {
              refresh_token: newRefreshToken,
            }),
          }),
        );

        api.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);

        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);

        store.dispatch(logout());

        showToast("Session expired. Please login again.", "error");

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // ================= Error Toast =================
    // Title = the API's generic message (e.g. "Invalid data") when present, else
    // a status-based default. Description = the specific reason, when available.
    const apiTitle =
      typeof data?.message === "string" && data.message.trim() ? data.message : null;
    const detail = getServerErrorDetail(data);

    // For 400 / 422 the server names the offending field: scroll to it.
    const isFieldError = status === 400 || status === 422;
    const serverField = isFieldError ? getServerErrorField(data) : null;

    const toastFor = (defaultTitle, fallbackDetail, toastType = "error") =>
      showToast(
        { title: apiTitle || defaultTitle, message: detail || fallbackDetail },
        toastType,
        3000,
        serverField ? { field: serverField } : {},
      );

    switch (status) {
      case 400:
        toastFor("Invalid Request", "Invalid request. Please check your input.");
        break;
      case 403:
        toastFor(
          "Access Denied",
          "You don't have permission to perform this action.",
          "warning",
        );
        break;
      case 404:
        toastFor("Not Found", "Requested resource not found.");
        break;
      case 422:
        toastFor("Validation Failed", "Validation failed. Please check your input.", "warning");
        break;
      case 429:
        toastFor("Too Many Requests", "Please slow down.", "warning");
        break;
      case 500: {
        const isGenericAxiosText = detail && /^request failed with status code/i.test(detail);
        showToast(
          {
            title: apiTitle || "Server Error",
            message: !isGenericAxiosText && detail ? detail : "Server error. Please try again later.",
          },
          "error",
        );
        break;
      }
      case 502:
      case 503:
      case 504:
        toastFor("Service Unavailable", "Please try again later.");
        break;
      default:
        toastFor("Error", "Something went wrong. Please try again.");
    }

    error.toastShown = true;

    return Promise.reject(error);
  },
);

// ================= Service =================

const service = {
  get: (url, params = {}, config = {}) => api.get(url, { params, ...config }),

  post: (url, data = {}, config = {}) => api.post(url, data, config),

  put: (url, data = {}, config = {}) => api.put(url, data, config),

  patch: (url, data = {}, config = {}) => api.patch(url, data, config),

  delete: (url, config = {}) => api.delete(url, config),

  upload: (url, data, config = {}) => api.post(url, data, config),

  patchForm: (url, data, config = {}) => api.patch(url, data, config),

  download: (url, config = {}) =>
    api.get(url, {
      ...config,
      responseType: "blob",
      headers: {
        Accept: "*/*",
        "Content-Type": false,
        ...(config.headers || {}),
      },
    }),
};

export default service;
